// Główny plik aplikacji - inicjalizacje zależności globalnych
import React, { useRef, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from '@mui/material';
import { SnackbarProvider } from 'notistack';
// Devtools tylko w dev – mniejszy bundle produkcyjny i mniej wykonywanego JS
const ReactQueryDevtools =
  process.env.NODE_ENV === 'development'
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    ? require('react-query/devtools').ReactQueryDevtools
    : () => null;
import { Provider as ReduxProvider } from 'react-redux';
import { Helmet } from 'react-helmet';

import { store } from 'store';
import { theme } from 'theme';
import { getHomeSlider } from 'api';

import Routes from './routes';
import { useAnalyticsTracking } from 'hooks';

import 'theme/global.scss';

// Konfiguracja klienta react-query
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    }
  }
});

const App = () => {
  const notistackRef = useRef<SnackbarProvider>(null);

  // Wczytaj pliki analytics tracking (wszystkie są opcjonalne)
  useAnalyticsTracking();

  // Preload obrazu LCP (pierwszy slajd) – prośba w dokumencie, fetchpriority=high, bez lazy load
  useEffect(() => {
    queryClient
      .fetchQuery(['home-slider'], getHomeSlider)
      .then((data: { items?: Array<{ image?: string }> } | undefined) => {
        const url = data?.items?.[0]?.image;
        if (url) {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = url;
          link.setAttribute('fetchpriority', 'high');
          document.head.appendChild(link);
        }
      })
      .catch(() => undefined);
  }, []);

  // Widget WCAG ładowany po idle – mniej konkurencji o main thread przy starcie (TBT)
  useEffect(() => {
    const loadWidget = () => {
      const widgetScript = document.createElement('script');
      widgetScript.type = 'module';
      widgetScript.src = 'https://widget.accessly.eu/widget';
      document.body.appendChild(widgetScript);
      setTimeout(() => {
        const widget = document.createElement('aa-widget');
        document.body.appendChild(widget);
      }, 100);
    };
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(loadWidget, { timeout: 3000 });
    } else {
      setTimeout(loadWidget, 2000);
    }
  }, []);

  return (
    <React.StrictMode>
      <Helmet>
        <title>WiseB2B</title>
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <ReduxProvider store={store}>
            <SnackbarProvider
              action={(key) => (
                <span
                  onClick={() => notistackRef.current?.closeSnackbar(key)}
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                />
              )}
              ref={notistackRef}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              disableWindowBlurListener>
              <BrowserRouter>
                <Routes />
              </BrowserRouter>
              <ReactQueryDevtools initialIsOpen={false} position="top-left" />
            </SnackbarProvider>
          </ReduxProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

export default App;
